/**
 * HousingData.ts
 * 
 * Data models, presets, catalog definitions, and types for Terra-Anima Housing System.
 */

import { Currency } from '../../core/Currency';

export type HousingTabType = 
  | 'claim'        // 1. 소유권표시 (Claim Land)
  | 'floors'       // 2. 바닥 (Floors)
  | 'walls'        // 3. 벽 (Walls)
  | 'furniture'    // 4. 가구 (Furniture)
  | 'props'        // 5. 소품 (Props/Decor)
  | 'exterior'     // 6. 외부인테리어 (Exterior)
  | 'facilities';  // 7. 외부시설 (Facilities/Butler/Farming)

export interface HousingCatalogItem {
  id: string;
  name: string;
  tab: HousingTabType;
  icon: string;
  desc: string;
  costCopper: number; // 아이템 자체 제작/구입 비용 (기본 0 또는 화폐)
  isObstacle?: boolean;
  subGridSnap?: boolean; // 소품용 16px 서브그리드 스냅 지원 여부
  categoryLabel: string;
  color?: string;
  // Canvas rendering callback
  draw: (ctx: CanvasRenderingContext2D, screenX: number, screenY: number, size: number, rot?: number) => void;
}

export interface PlacedHousingObject {
  id: string;
  catalogId: string;
  tab: HousingTabType;
  name: string;
  x: number;          // World tile X (can be fractional for subGridSnap, e.g. 10.5)
  y: number;          // World tile Y
  rotation: number;   // 0, 90, 180, 270 degrees
  isObstacle: boolean;
  placedBy: string;   // Owner user ID or GM
  timestamp: number;
}

export interface LandOwnership {
  x: number;
  y: number;
  ownerId: string;
  ownerName: string;
  zoneId: number;
  claimedAt: number;
  pricePaidGold: number;
}

export interface MailboxTaxData {
  depositedCopper: number; // 우체통 금고에 예치된 집세/세금 (Copper 단위)
  lastTaxPaidAt: number;
  unpaidCycles: number;
  letters: Array<{
    id: string;
    sender: string;
    title: string;
    content: string;
    goldAttached: number;
    receivedAt: number;
  }>;
}

/** Land price: 10 Gold per tile (converted to copper: 10 * 1,000,000 = 10,000,000 Copper) */
export const LAND_TILE_PRICE_GOLD = 10;
export const LAND_TILE_PRICE_COPPER = Currency.toCopper({ gold: LAND_TILE_PRICE_GOLD });

/** NPC List for Admin Land Assignment Dropdown */
export interface HousingOwnerOption {
  id: string;
  name: string;
  roleType: 'player' | 'npc' | 'royal';
}

export const HOUSING_PRESET_NPCS: HousingOwnerOption[] = [
  { id: 'npc_king_solaris_3', name: '👑 왕, 솔라리스 3세 (King Solaris III)', roleType: 'royal' },
  { id: 'npc_chancellor_elion', name: '📜 재상 엘리온', roleType: 'npc' },
  { id: 'npc_commander_arthur', name: '⚔️ 근위총사령관 아서', roleType: 'npc' },
  { id: 'npc_butler_sebastian', name: '🤵 공인 수석집사 세바스찬', roleType: 'npc' },
  { id: 'npc_stable_jackson', name: '🐎 마구간지기 잭슨', roleType: 'npc' },
  { id: 'npc_enchanter_morgan', name: '🔮 수석 인챈터 모르간', roleType: 'npc' },
];
